import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type { WorkLogEventRow } from '../database.types';

/**
 * A Capture's Work Log, newest first. M6 / S06-01.
 *
 * AUTHORIZATION IS RLS, NOT A CLIENT CHECK — the same rule `use-capture.ts`
 * and `use-capture-photos.ts` follow. Another user's events return no rows,
 * and the id in the URL grants nothing.
 *
 * **THE ORDER IS `occurred_at DESC, seq DESC`, AND THE TIE-BREAKER IS NOT
 * OPTIONAL.** Two events written in the same transaction — a photo attach and
 * its `photo_added`, or any future pair — can carry the SAME `occurred_at` to
 * the microsecond. Ordering by time alone leaves those rows in whatever order
 * Postgres happens to return, so a history could read differently on two
 * loads of the same Capture. `seq` is the per-Capture counter that exists for
 * exactly this, and `unique (capture_id, seq)` guarantees it never ties.
 *
 * NO PAGINATION, NO FILTERS, NO INFINITE SCROLL. A Capture's history is small
 * and finite; the first thing that would make it unreadable is machinery.
 */

export type CaptureWorkLogStatus = 'loading' | 'ready' | 'failed';

export interface CaptureWorkLogState {
  status: CaptureWorkLogStatus;
  events: WorkLogEventRow[];
  error: string | null;
  reload: () => Promise<void>;
}

type FetchResult = { kind: 'ready'; rows: WorkLogEventRow[] } | { kind: 'failed' };

export function useCaptureWorkLog(captureId: string | undefined): CaptureWorkLogState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<CaptureWorkLogStatus>(client ? 'loading' : 'failed');
  const [events, setEvents] = useState<WorkLogEventRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  const fetchEvents = useCallback(async (): Promise<FetchResult> => {
    if (!client || !captureId) return { kind: 'ready', rows: [] };

    const { data, error: queryError } = await client
      .from('work_log_event')
      .select('*')
      .eq('capture_id', captureId)
      .order('occurred_at', { ascending: false })
      .order('seq', { ascending: false });

    if (queryError) {
      reportError(queryError, 'captureWorkLog.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client, captureId]);

  const apply = useCallback((result: FetchResult) => {
    if (result.kind === 'failed') {
      // The history failing to load must not take the Capture down with it —
      // the transcript and the photos above are still readable, and saying so
      // beats a blank panel that looks like an empty history.
      setError('Could not load this Capture’s history. Check your connection and try again.');
      setStatus('failed');
      return;
    }
    setEvents(result.rows);
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchEvents().then((result) => {
      if (!cancelled) apply(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchEvents, apply]);

  const reload = useCallback(async () => {
    apply(await fetchEvents());
  }, [fetchEvents, apply]);

  return { status, events, error, reload };
}
